import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import layerAdmin, { CompanySettings, labelOf, LayerDetail, LayerSummary } from '@/services/layerAdmin.service';
import { removeCompanyLogo, uploadCompanyLogo } from '@/services/companyLogo.service';
import { useLogoSource } from '@/components/map/CompanyLogo';

/**
 * Which of our platform layers a company sees, and which of their columns it does not. GeoNexora's taxonomy
 * (geonexora-server, Veramenta docs/en/60 sec.6) - never UMS, and never Veramenta. Used when a company is
 * opened and on its Data tab.
 */

const input = 'w-full px-3 py-2 border border-gn-light rounded bg-gn-surface text-gn-primary';
const button = 'px-3 py-2 border border-gn-light rounded hover:bg-gn-tertiary disabled:opacity-50 text-sm';

export type Grants = Record<string, string[]>;

interface Props {
  layers: LayerSummary[];
  grants: Grants;
  onChange: (grants: Grants) => void;
  /** Companies to copy a selection from (by id and name). */
  copyFrom?: { tenant: string; name: string }[];
}

export const LayerGrantPicker: React.FC<Props> = ({ layers, grants, onChange, copyFrom = [] }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  const [filter, setFilter] = useState('');
  const [source, setSource] = useState('');
  const [columnsOf, setColumnsOf] = useState<string | null>(null);
  const { data: openLayer } = useQuery<LayerDetail>({
    queryKey: ['adminLayer', columnsOf],
    queryFn: () => layerAdmin.layer(columnsOf!),
    enabled: !!columnsOf,
  });

  /** One entry per layer type: a type drawn by several layers (borders and their labels) is granted once. */
  const types = useMemo(() => {
    const byType = new Map<string, { typeKey: string; names: string[]; menu: string; key: string }>();
    for (const l of layers) {
      const e = byType.get(l.typeKey);
      const name = labelOf(l.labels, lang, l.name);
      if (e) e.names.push(name);
      else byType.set(l.typeKey, { typeKey: l.typeKey, names: [name], menu: l.menu?.split('/')[0] || '—', key: l.key });
    }
    const f = filter.trim().toLowerCase();
    const groups = new Map<string, { typeKey: string; names: string[]; key: string }[]>();
    for (const e of byType.values()) {
      if (f && !`${e.names.join(' ')} ${e.typeKey}`.toLowerCase().includes(f)) continue;
      groups.set(e.menu, [...(groups.get(e.menu) ?? []), e]);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [layers, filter, lang]);

  const toggle = (next: Grants, typeKey: string, on: boolean) => {
    if (on) next[typeKey] = next[typeKey] ?? [];
    else delete next[typeKey];
  };

  const toggleHidden = (typeKey: string, column: string, hidden: boolean) => {
    const was = grants[typeKey] ?? [];
    onChange({ ...grants, [typeKey]: hidden ? [...new Set([...was, column])] : was.filter((c) => c !== column) });
  };

  const copy = async () => {
    const from = await layerAdmin.company(source);
    onChange({ ...(from.grants ?? {}) });
  };

  return (
    <div>
      <div className="flex gap-2 items-center mb-3">
        <input className={input} placeholder={t('companies.filter')} value={filter} onChange={(e) => setFilter(e.target.value)} />
        {copyFrom.length > 0 && (
          <>
            <select className="px-3 py-2 border border-gn-light rounded bg-gn-surface" value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="">{t('companies.copy_from')}</option>
              {copyFrom.map((c) => <option key={c.tenant} value={c.tenant}>{c.name}</option>)}
            </select>
            <button type="button" disabled={!source} onClick={copy} className={button}>{t('companies.copy')}</button>
          </>
        )}
      </div>
      <p className="text-sm mb-2">{t('companies.layer_count', { count: Object.keys(grants).length })}</p>
      <div className="flex gap-4">
        <div className="flex-1 space-y-4">
          {types.map(([menu, group]) => {
            const all = group.every((l) => l.typeKey in grants);
            return (
              <fieldset key={menu} className="border border-gn-light rounded p-3">
                <legend className="px-1 font-medium">
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={all} onChange={() => {
                      const next = { ...grants };
                      group.forEach((l) => toggle(next, l.typeKey, !all));
                      onChange(next);
                    }} />
                    {menu}
                  </label>
                </legend>
                <div className="grid grid-cols-2 gap-1">
                  {group.map((l) => (
                    <div key={l.typeKey} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={l.typeKey in grants} onChange={(e) => {
                        const next = { ...grants };
                        toggle(next, l.typeKey, e.target.checked);
                        onChange(next);
                      }} />
                      <span className="truncate">{l.names.join(' · ')}</span>
                      {l.typeKey in grants && (
                        <button type="button" className="text-xs underline text-gn-muted" onClick={() => setColumnsOf(l.key)}>
                          {(grants[l.typeKey] ?? []).length > 0
                            ? t('companies.hidden_count', { count: grants[l.typeKey].length })
                            : t('companies.columns')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
        {columnsOf && openLayer && (
          <div className="w-72 shrink-0 border border-gn-light rounded p-3 self-start sticky top-0">
            <div className="flex justify-between items-center mb-2">
              <b className="text-sm">{labelOf(openLayer.map.labels, lang, openLayer.name)}</b>
              <button type="button" onClick={() => setColumnsOf(null)}>×</button>
            </div>
            <p className="text-xs text-gn-muted mb-2">{t('companies.hidden_hint')}</p>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {openLayer.fields.filter((f) => f.type !== 'geometry').map((f) => {
                const hidden = (grants[openLayer.typeKey] ?? []).includes(f.column);
                return (
                  <label key={f.key} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!hidden} onChange={(e) => toggleHidden(openLayer.typeKey, f.column, !e.target.checked)} />
                    <span className="truncate">{labelOf(f.labels, lang, f.column)}</span>
                    {f.fieldClass !== 'none' && <span className="text-xs px-1 rounded bg-gn-surface">{f.fieldClass}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * A company's analyses left, where its map opens and its logo.
 *
 * The start view is latitude and longitude in degrees (WGS 84), as Google Maps shows them ("41.0082, 28.9784":
 * latitude first). A pair can be pasted into the latitude box. Both or neither: empty means the default view.
 */
export const CompanySettingsFields: React.FC<{
  settings: Partial<CompanySettings>;
  onChange: (s: Partial<CompanySettings>) => void;
  /** The company, once it exists: the logo is uploaded to it straight away. */
  tenant?: string;
}> = ({ settings, onChange, tenant }) => {
  const { t } = useTranslation();
  const [logoError, setLogoError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const logoSrc = useLogoSource(settings.logo ?? null);

  const credits = (key: 'driveCredits' | 'walkCredits' | 'motorCredits', label: string) => {
    const value = settings[key] ?? -1;
    const unlimited = value < 0;
    return (
      <div>
        <span className="text-sm">{label}</span>
        <div className="flex gap-2 items-center">
          <input type="number" min={0} className={input} disabled={unlimited} value={unlimited ? '' : value}
                 onChange={(e) => onChange({ ...settings, [key]: Math.max(0, Number(e.target.value)) })} />
          <label className="inline-flex items-center gap-1 text-sm whitespace-nowrap">
            <input type="checkbox" checked={unlimited} onChange={(e) => onChange({ ...settings, [key]: e.target.checked ? -1 : 0 })} />
            {t('companies.unlimited')}
          </label>
        </div>
      </div>
    );
  };

  const num = (v: string): number | null => {
    const n = Number(v.replace(',', '.').trim());
    return v.trim() === '' || !Number.isFinite(n) ? null : n;
  };
  const setLat = (v: string) => {
    // "41.0082, 28.9784" (as Google Maps copies it): latitude, longitude.
    const pair = v.match(/^\s*(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (pair) {
      onChange({ ...settings, mapLat: Number(pair[1]), mapLon: Number(pair[2]), mapZoom: settings.mapZoom ?? 11 });
      return;
    }
    onChange({ ...settings, mapLat: num(v) });
  };
  const lat = settings.mapLat;
  const lon = settings.mapLon;
  const viewProblem =
    (lat == null) !== (lon == null) ? t('companies.map_both')
      : lat != null && (Math.abs(lat) > 90) ? t('companies.map_lat_range')
        : lon != null && (Math.abs(lon) > 180) ? t('companies.map_lon_range')
          : null;
  const preview = lat != null && lon != null && !viewProblem
    ? `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=${settings.mapZoom ?? 11}/${lat}/${lon}`
    : null;

  const upload = async (file: File | undefined) => {
    setLogoError(null);
    if (!file || !tenant) return;
    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type)) { setLogoError(t('companies.logo_type')); return; }
    if (file.size > 256 * 1024) { setLogoError(t('companies.logo_size')); return; }
    setUploading(true);
    try {
      const logo = await uploadCompanyLogo(tenant, file);
      onChange({ ...settings, logo });
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };
  const removeLogo = async () => {
    setLogoError(null);
    try {
      if (tenant) await removeCompanyLogo(tenant);
      onChange({ ...settings, logo: '' });
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {credits('driveCredits', t('companies.drive_credits'))}
        {credits('walkCredits', t('companies.walk_credits'))}
        {credits('motorCredits', t('companies.motor_credits'))}
      </div>

      <fieldset className="border border-gn-light rounded p-3">
        <legend className="px-1 text-sm font-medium">{t('companies.map_view')}</legend>
        <div className="grid grid-cols-3 gap-4">
          <label className="block text-sm">{t('companies.latitude')}
            <input className={input} inputMode="decimal" placeholder="41.0082" value={lat ?? ''}
                   onChange={(e) => setLat(e.target.value)} />
          </label>
          <label className="block text-sm">{t('companies.longitude')}
            <input className={input} inputMode="decimal" placeholder="28.9784" value={lon ?? ''}
                   onChange={(e) => onChange({ ...settings, mapLon: num(e.target.value) })} />
          </label>
          <label className="block text-sm">{t('companies.zoom')}
            <input type="number" min={1} max={20} className={input} placeholder="11" value={settings.mapZoom ?? ''}
                   onChange={(e) => onChange({ ...settings, mapZoom: num(e.target.value) == null ? null : Math.min(20, Math.max(1, Math.round(Number(e.target.value)))) })} />
          </label>
        </div>
        <p className="text-xs text-gn-muted mt-2">{t('companies.map_hint')}</p>
        {viewProblem && <p className="text-xs text-gn-danger mt-1">{viewProblem}</p>}
        <div className="flex gap-3 mt-1 text-xs">
          {preview && <a className="text-gn-blue underline" href={preview} target="_blank" rel="noreferrer">{t('companies.map_check')}</a>}
          {(lat != null || lon != null) && (
            <button type="button" className="underline text-gn-muted" onClick={() => onChange({ ...settings, mapLat: null, mapLon: null, mapZoom: null })}>
              {t('companies.map_clear')}
            </button>
          )}
        </div>
      </fieldset>

      <fieldset className="border border-gn-light rounded p-3">
        <legend className="px-1 text-sm font-medium">{t('companies.logo')}</legend>
        <div className="flex items-center gap-4">
          <div className="w-40 h-14 rounded border border-dashed border-gn-light flex items-center justify-center bg-gn-surface overflow-hidden">
            {logoSrc
              ? <img src={logoSrc} alt="" className="max-h-12 max-w-[150px] object-contain" />
              : <span className="text-xs text-gn-muted">{t('companies.logo_none')}</span>}
          </div>
          {tenant ? (
            <div className="flex flex-col gap-1 text-sm">
              <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-gn-light rounded cursor-pointer hover:bg-gn-tertiary w-fit">
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" disabled={uploading}
                       onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
                {uploading ? '…' : t('companies.logo_upload')}
              </label>
              {settings.logo && (
                <button type="button" className="text-xs underline text-gn-muted w-fit" onClick={() => void removeLogo()}>
                  {t('companies.logo_remove')}
                </button>
              )}
              <span className="text-xs text-gn-muted">{t('companies.logo_hint')}</span>
            </div>
          ) : (
            <span className="text-xs text-gn-muted">{t('companies.logo_after_create')}</span>
          )}
        </div>
        {logoError && <p className="text-xs text-gn-danger mt-2">{logoError}</p>}
      </fieldset>
    </div>
  );
};

export default LayerGrantPicker;
